import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import {
  ExtractSkeleton,
  ScreenGreeting,
  TransactionListSkeleton,
} from "../../../Components/Molecules";
import { GridPagination } from "../../../Components/Molecules/GridHistory";
import { DateAmountSubAccountApiFilterObj } from "../../../Components/Molecules/GridHistory/filterTypes";
import {
  BAAS_STATEMENT_PAGE_SIZE,
  MAX_STATEMENT_ITEMS,
  useTransactionsData,
} from "../../../Hooks";
import { useFilter } from "../../../Hooks/useFilter";
import { ExtractDayTimeline } from "../Extract/ExtractDayTimeline";
import { ExtractTimelineToggleAll } from "../Extract/ExtractTimelineToggleAll";
import { paginateList } from "../Extract/extractFilters";
import { isStatementRefund } from "../../../Utils/buildReceiptFromStatementItem";
import { RefundsFilterBar } from "./RefundsFilterBar";
import {
  applyRefundsClientFilters,
  filtersFromRefundsFilterObj,
  flattenTransactionDayGroups,
  refundsFilterObjFromFilters,
  regroupTransactionsByDay,
} from "./refundsFilters";

const DEFAULT_ROWS_PER_PAGE = 15;
const EMPTY_SUB_ACCOUNTS = [];

export const Refunds = () => {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(DEFAULT_ROWS_PER_PAGE);
  const [filters, setFilters] = useState(null);
  const [allExpanded, setAllExpanded] = useState(true);

  const {
    state: transactionsData,
    handleGET: fetchTransactionsGET,
    loading,
  } = useTransactionsData();

  const { getFilterObj, handleApplyFilters } = useFilter();
  const refundsFilterObj = getFilterObj("refunds") as DateAmountSubAccountApiFilterObj;

  const buildQuery = useCallback(
    () => ({
      page: 1,
      page_size: BAAS_STATEMENT_PAGE_SIZE,
      fetch_all: true,
      max_items: MAX_STATEMENT_ITEMS,
      start_date: refundsFilterObj.start_date || "",
      end_date: refundsFilterObj.end_date || "",
    }),
    [refundsFilterObj.start_date, refundsFilterObj.end_date],
  );

  useEffect(() => {
    setFilters(filtersFromRefundsFilterObj(refundsFilterObj));
  }, [
    refundsFilterObj.start_date,
    refundsFilterObj.end_date,
    refundsFilterObj.start_amount,
    refundsFilterObj.end_amount,
    refundsFilterObj.sub_account_id,
  ]);

  useEffect(() => {
    fetchTransactionsGET(buildQuery());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refundsFilterObj.start_date, refundsFilterObj.end_date]);

  const flatItems = useMemo(() => {
    const all = flattenTransactionDayGroups(transactionsData?.items);
    return applyRefundsClientFilters(
      all.filter((item) => isStatementRefund(item)),
      filters ?? {},
    );
  }, [transactionsData?.items, filters]);

  const paginatedItems = useMemo(
    () => paginateList(flatItems, page, rowsPerPage),
    [flatItems, page, rowsPerPage],
  );

  const displayGroups = useMemo(
    () => regroupTransactionsByDay(paginatedItems),
    [paginatedItems],
  );

  const timelineDayKey = useMemo(
    () =>
      displayGroups
        .map((group) => (group ? Object.keys(group)[0] : null))
        .filter(Boolean)
        .join("|"),
    [displayGroups],
  );

  useEffect(() => {
    setAllExpanded(true);
  }, [timelineDayKey, page]);

  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(flatItems.length / rowsPerPage) || 1);
    if (page > maxPage) setPage(maxPage);
  }, [flatItems.length, rowsPerPage, page]);

  const handleFiltersChange = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    handleApplyFilters("refunds", refundsFilterObjFromFilters(nextFilters));
  };

  const handleRowsPerPageChange = (value) => {
    setRowsPerPage(value);
    setPage(1);
  };

  const hasApiFilters = Boolean(
    refundsFilterObj.start_date
    || refundsFilterObj.end_date
    || refundsFilterObj.start_amount
    || refundsFilterObj.end_amount
    || refundsFilterObj.sub_account_id,
  );

  const hasItems = (transactionsData?.items?.length ?? 0) > 0;
  const isInitialLoading = (loading.GET && !hasItems) || filters === null;

  return (
    <div className="w-full">
      <ScreenGreeting
        title={intl.get("refunds.title")}
        subTitle={intl.get(`phrases.refunds`, {
          total: flatItems.length,
        })}
      />

      {isInitialLoading ? (
        <ExtractSkeleton />
      ) : (
        <section>
          <div className="mb-4">
            <RefundsFilterBar
              filters={filters}
              onFiltersChange={handleFiltersChange}
              filteredItems={flatItems}
              subAccounts={EMPTY_SUB_ACCOUNTS}
            />
          </div>

          <div className="w-full">
            {loading.GET ? (
              <TransactionListSkeleton rows={hasApiFilters ? 8 : 12} withBalanceRows />
            ) : (
              <>
                {displayGroups.length > 0 && (
                  <ExtractTimelineToggleAll
                    allExpanded={allExpanded}
                    onToggle={() => setAllExpanded((prev) => !prev)}
                  />
                )}
                <ExtractDayTimeline
                  dayGroups={displayGroups}
                  allExpanded={allExpanded}
                  onAllExpandedChange={setAllExpanded}
                />
                <div className="mt-4">
                  <GridPagination
                    total={flatItems.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    currentPageCount={paginatedItems.length}
                    onPageChange={setPage}
                    onRowsPerPageChange={handleRowsPerPageChange}
                    itemLabel={intl.get("common.transactions")}
                  />
                </div>
              </>
            )}
          </div>
        </section>
      )}
    </div>
  );
};
